import React from 'react';
import { isPricingModelCpmOrVcpm } from 'modules/campaigns/config/pricingModel';
import { BidField } from 'modules/campaigns/modules/common-campaign-form/components/Bid';
import { BidTooltip } from 'modules/campaigns/modules/common-campaign-form/components/Bid/BidTooltip';
import { BidAdjustmentsSection } from 'modules/campaigns/modules/common-campaign-form/components/BidAdjustmentsSection/BidAdjustmentsSection';
import { BidStrategyV2 } from 'modules/campaigns/modules/common-campaign-form/components/BidStrategyV2/BidStrategyV2';
import { useHighImpactPackageIndication } from 'modules/campaigns/modules/common-campaign-form/components/BidStrategyV2/hooks/useHighImpactPackageIndication';
import { useShouldRenderBidStrategyV2 } from 'modules/campaigns/modules/common-campaign-form/components/BidStrategyV2/hooks/useShouldRenderBidStrategyV2';
import { CampaignBudget } from 'modules/campaigns/modules/common-campaign-form/components/Budget/CampaignBudget';
import { useIsCampaignPartOfFullySharedBudget } from 'modules/campaigns/modules/common-campaign-form/components/Budget/hooks/useIsCampaignPartOfFullySharedBudget';
import { BudgetSectionFrame } from 'modules/campaigns/modules/common-campaign-form/components/BudgetSectionFrame';
import { useCampaignBudgetRestrictions } from 'modules/campaigns/modules/common-campaign-form/components/BudgetSectionSponsored/hooks/useCampaignBudgetRestrictions';
import CpaGoal from 'modules/campaigns/modules/common-campaign-form/components/CpaGoal/CpaGoal';
import CreativeOptimization from 'modules/campaigns/modules/common-campaign-form/components/CreativeOptimization/CreativeOptimization';
import ProfitMargin from 'modules/campaigns/modules/common-campaign-form/components/ProfitMargin/ProfitMargin';
import { SiteExplorationField } from 'modules/campaigns/modules/common-campaign-form/components/SiteExploration/SiteExplorationField';
import { SpendGuardField } from 'modules/campaigns/modules/common-campaign-form/components/SpendGuard/SpendGuardField';
import { FEATURE_FLAGS, useConfigMatch } from 'modules/taboola-common-frontend-modules/account-configurations';
import { usePermissions } from 'modules/taboola-common-frontend-modules/authentication';
import { useFormDataContext, useFormFieldValue } from 'modules/taboola-common-frontend-modules/formData';
import { FormattedMessage } from 'modules/taboola-common-frontend-modules/i18n';

const BudgetSectionSponsored = ({ onlyBudget, isInitiallyCollapsed } = {}) => {
    const {
        formAccount: { currency },
    } = useFormDataContext();
    const { value: pricingModel } = useFormFieldValue({ field: 'pricingModel' });
    const { value: targetCpaData } = useFormFieldValue({ field: 'targetCpaData' });
    const isTargetCpaEnabled = useConfigMatch({ [FEATURE_FLAGS.TARGET_CPA_ENABLED]: 'true' });
    const isSupplyExplorationEnabled = useConfigMatch({ [FEATURE_FLAGS.SUPPLY_EXPLORATION_ENABLED]: 'true' });
    const isSpendGuardActiveOnAccountLevel = useConfigMatch({
        [FEATURE_FLAGS.SAFETY_NETS_ENABLED_CONFIG_ATTRIBUTE]: 'true',
    });
    const supplyExplorationPermission = usePermissions('SUPPLY_EXPLORATION');
    const shouldRenderSpendGuardActivation = usePermissions('SAFETY_NETS_VIEW') && isSpendGuardActiveOnAccountLevel;

    const shouldRenderCpaGoal = !targetCpaData && !isTargetCpaEnabled;
    const shouldRenderSupplyExploration = isSupplyExplorationEnabled && supplyExplorationPermission;
    const hasHighImpactPackage = useHighImpactPackageIndication();
    const shouldRenderBidStrategyV2 = useShouldRenderBidStrategyV2();
    const budgetRestrictions = useCampaignBudgetRestrictions();
    const isBudgetHidden = useIsCampaignPartOfFullySharedBudget();

    if (onlyBudget) {
        return (
            <BudgetSectionFrame isBudgetHidden={isBudgetHidden} isInitiallyCollapsed={isInitiallyCollapsed}>
                {shouldRenderBidStrategyV2 && <BidStrategyV2 />}
                <CampaignBudget budgetRestrictions={budgetRestrictions} />
            </BudgetSectionFrame>
        );
    }

    return (
        <BudgetSectionFrame isBudgetHidden={isBudgetHidden} isInitiallyCollapsed={isInitiallyCollapsed}>
            {shouldRenderBidStrategyV2 && <BidStrategyV2 />}
            <CampaignBudget budgetRestrictions={budgetRestrictions} />
            {!shouldRenderBidStrategyV2 && (
                <BidField
                    label={<FormattedMessage id="campaign.editor.bid.title" defaultMessage="Bid" />}
                    description={
                        <>
                            <FormattedMessage
                                id="campaign.editor.bid.description"
                                defaultMessage="Select delivery method and bid amount for your campaign."
                            />
                            {hasHighImpactPackage ? (
                                <>
                                    <br />
                                    <FormattedMessage
                                        id="campaign.editor.bid.highImpactException"
                                        defaultMessage="When choosing a High Impact Package the pricing model must be vCPM."
                                    />
                                </>
                            ) : null}
                        </>
                    }
                    helpText={<BidTooltip hasHighImpactPackage={hasHighImpactPackage} />}
                    hasHighImpactPackage={hasHighImpactPackage}
                />
            )}
            <ProfitMargin />
            {!isPricingModelCpmOrVcpm(pricingModel) && (
                <>
                    <CreativeOptimization />
                    {shouldRenderCpaGoal && <CpaGoal currency={currency} />}
                </>
            )}
            {shouldRenderSupplyExploration && <SiteExplorationField />}
            {shouldRenderSpendGuardActivation && <SpendGuardField />}
            <BidAdjustmentsSection />
        </BudgetSectionFrame>
    );
};

export default BudgetSectionSponsored;
